import { Component, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Copy, Download, ImageDown, Mail, MessageCircle, Printer } from "lucide-react";
import { money } from "../lib/api";
import { shareQrToWhatsapp, canCopyQrImageToClipboard, copyQrImageToClipboard } from "../lib/pixShare";

// PIX por chave — modal de exibição do QR estático + Copia e Cola,
// compartilhado entre Faturamento.jsx (operacional) e Financeiro.jsx
// (mensalidade SaaS). Nunca reaproveitado pelo fluxo PIX integrado
// (hoje Mercado Pago, mas a arquitetura é multiprovedor) — este é
// gerado 100% localmente pela LOOTUS, sem integração bancária, e nunca
// expira no sentido bancário do termo.
//
// Correção pós-homologação (item 3) — o QR não estava renderizando:
// "qrcode.react" era carregado via require() dentro de um try/catch no
// topo do módulo. O build CJS da biblioteca usa o helper de interop do
// esbuild (`__toESM(require("react"))`) para expor `React.default`; sob
// o resolvedor de módulos do webpack/CRA esse wrapper não reconhecia o
// React já carregado pelo bundle como teria em Node puro, e a própria
// obtenção do componente lançava uma exceção — sempre engolida pelo
// try/catch, então QRCodeCanvas ficava permanentemente `null` e a tela
// caía sempre no fallback "QR Code indisponível", mesmo com o
// pix_brcode gerado corretamente. Um import estático de verdade (linha
// acima) usa a build ESM da biblioteca (`import React from "react"`,
// sem nenhum wrapper de interop) e deixa a resolução do módulo a cargo
// do próprio bundler — o mesmo caminho, testado, usado por qualquer
// outro import deste projeto. Nunca depende de rede nem do Mercado
// Pago: o QR é só a representação visual do pix_brcode já gerado 100%
// localmente no backend.

// Rede de segurança para uma exceção REAL e inesperada durante a
// renderização do QR (ex.: um valor de brcode que a biblioteca não
// consiga codificar) — nunca para o caso normal de "ainda não temos
// brcode" (esse é tratado à parte, sem cair aqui). Só nesta situação
// excepcional é que a mensagem "QR Code indisponível" volta a aparecer.
class QrRenderBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error("PixChaveModal: falha ao renderizar o QR Code", error);
  }
  render() {
    if (this.state.hasError) {
      return <p className="text-sm text-slate-500 py-8">QR Code indisponível — use o Copia e Cola abaixo.</p>;
    }
    return this.props.children;
  }
}

export default function PixChaveModal({ open, onOpenChange, title, valor, recebedor, instituicao, brcode, onCopy, onGetWhatsappLink, onShareEmail, filename = "pix-qrcode.png" }) {
  const canvasWrapRef = useRef(null);
  const [sharingWhatsapp, setSharingWhatsapp] = useState(false);
  const [copyingQrImage, setCopyingQrImage] = useState(false);

  const downloadQr = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = filename;
    a.click();
  };

  // Correção — Modal Faturamento/Compartilhamento, item 2 — Copiar QR
  // Code COMO IMAGEM (Clipboard API), uma das ações de fallback pedidas
  // quando o navegador não suporta anexar via Web Share. Mesmo QR já
  // renderizado acima, nunca gera um novo.
  const copiarQrComoImagem = async () => {
    setCopyingQrImage(true);
    try {
      const canvas = canvasWrapRef.current?.querySelector("canvas");
      const result = await copyQrImageToClipboard({ canvas });
      if (result.copied) toast.success("QR Code copiado como imagem.");
      else toast.error("Não foi possível copiar o QR Code como imagem.");
    } finally {
      setCopyingQrImage(false);
    }
  };

  // Item 3 do pedido — compartilha o MESMO QR já renderizado acima (nunca
  // gera um novo): cria um File real (qr-pix-<cobrança>.png) e tenta a
  // Web Share API (navigator.canShare -> navigator.share) com texto +
  // arquivo anexado — abre o share sheet nativo do sistema, nunca um
  // link wa.me nesse caminho. Sem suporte, abre o WhatsApp só com o
  // texto e NUNCA dispara download automático (ajuste complementar, item
  // 2) — avisa claramente e aponta para "Copiar QR Code"/"Baixar QR
  // Code" ao lado, pra anexar à mão quem quiser.
  const shareWhatsappComQr = async () => {
    if (!onGetWhatsappLink) return;
    setSharingWhatsapp(true);
    try {
      const waLink = await onGetWhatsappLink();
      const canvas = canvasWrapRef.current?.querySelector("canvas");
      const result = await shareQrToWhatsapp({ canvas, waLink, filename });
      if (result.shared) toast.success("QR Code e mensagem compartilhados.");
      else if (result.cancelled) { /* usuário cancelou a folha de compartilhamento — nada a avisar */ }
      else if (result.fallback && result.reason === "sem-suporte-anexo") toast.info("Seu navegador não permite anexar automaticamente o QR Code ao WhatsApp. WhatsApp aberto com a mensagem — copie ou baixe o QR Code ao lado para anexar manualmente.", { duration: 8000 });
      else toast.info("WhatsApp aberto com a mensagem pronta — confirme o envio por lá.");
    } catch {
      toast.error("Não foi possível preparar o compartilhamento por WhatsApp.");
    } finally {
      setSharingWhatsapp(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pix-chave-modal">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-center">
          <div ref={canvasWrapRef} className="flex justify-center">
            {brcode ? (
              <QrRenderBoundary>
                <QRCodeCanvas value={brcode} size={192} className="border border-slate-200 rounded-md p-2 bg-white" />
              </QrRenderBoundary>
            ) : (
              <p className="text-sm text-slate-500 py-8">Gerando QR Code…</p>
            )}
          </div>
          <div className="text-left text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
            <div><span className="text-slate-500">Valor:</span> <span className="font-medium text-slate-900">{money(valor)}</span></div>
            <div><span className="text-slate-500">Recebedor:</span> <span className="font-medium text-slate-900">{recebedor || "-"}</span></div>
            {instituicao && <div><span className="text-slate-500">Instituição:</span> <span className="font-medium text-slate-900">{instituicao}</span></div>}
          </div>
          <div className="text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded-md p-3 text-left flex items-start gap-2">
            <Badge className="bg-violet-100 text-violet-800 shrink-0">PIX por chave</Badge>
            <span>Este QR Code não expira automaticamente pelo banco. A confirmação do pagamento é manual, feita pelo recebedor após verificar o depósito.</span>
          </div>
          {brcode && (
            <div className="text-left">
              <div className="text-xs text-slate-500 mb-1">Pix Copia e Cola</div>
              <div className="font-mono text-xs bg-slate-50 border border-slate-200 rounded-md p-3 break-all" data-testid="pix-chave-copia-cola">{brcode}</div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {brcode && (
            <Button variant="outline" onClick={() => onCopy(brcode, "Código Pix")} data-testid="pix-chave-copiar">
              <Copy size={14} /> Copiar código Pix
            </Button>
          )}
          {brcode && (
            <Button variant="outline" onClick={downloadQr} data-testid="pix-chave-baixar-qr">
              <Download size={14} /> Baixar QR Code
            </Button>
          )}
          {brcode && canCopyQrImageToClipboard() && (
            <Button variant="outline" onClick={copiarQrComoImagem} disabled={copyingQrImage} data-testid="pix-chave-copiar-qr-imagem">
              <ImageDown size={14} /> {copyingQrImage ? "Copiando..." : "Copiar QR Code"}
            </Button>
          )}
          {onGetWhatsappLink && (
            <Button variant="outline" onClick={shareWhatsappComQr} disabled={sharingWhatsapp} data-testid="pix-chave-whatsapp">
              <MessageCircle size={14} /> {sharingWhatsapp ? "Preparando..." : "WhatsApp"}
            </Button>
          )}
          {onShareEmail && (
            <Button variant="outline" onClick={onShareEmail} data-testid="pix-chave-email">
              <Mail size={14} /> E-mail
            </Button>
          )}
          <Button variant="outline" onClick={() => window.print()} data-testid="pix-chave-imprimir">
            <Printer size={14} /> Imprimir
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="pix-chave-fechar">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
